import { lazy, Suspense, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { LocaleProvider } from "@/i18n/LocaleProvider";
import { AuthProvider } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import Index from "./pages/Index";
import ProtectedRoute from "./components/ProtectedRoute";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
// Lazy load non-critical public pages
const About = lazy(() => import("./pages/About"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Capabilities = lazy(() => import("./pages/Capabilities"));
const Quality = lazy(() => import("./pages/Quality"));
const News = lazy(() => import("./pages/News"));
// Per-article route — fixes the SEO regression where 360 sitemap URLs
// all resolved to /news (the index). Each article now has its own
// canonical URL + NewsArticle JSON-LD schema + Open Graph article tags.
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
// Tag-cluster landing pages — /news/tag/europe, /news/tag/faq,
// /news/tag/fda, etc. Each is a focused CollectionPage with its own
// JSON-LD ItemList and unique meta. 18 tags currently defined; the
// most-trafficked clusters get individual sitemap entries.
const NewsTag = lazy(() => import("./pages/NewsTag"));
const Contact = lazy(() => import("./pages/Contact"));
const TherapeuticArea = lazy(() => import("./pages/TherapeuticArea"));
const Checkout = lazy(() => import("./pages/Checkout"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ReturnPolicy = lazy(() => import("./pages/ReturnPolicy"));
const Certifications = lazy(() => import("./pages/Certifications"));
const Markets = lazy(() => import("./pages/Markets"));
const SampleRequest = lazy(() => import("./pages/SampleRequest"));
const CountryPage = lazy(() => import("./pages/Country"));
// Lazy import — the modal mounts itself when triggered, so don't ship
// it in the initial bundle.
const CatalogLeadMagnet = lazy(() => import("./components/CatalogLeadMagnet"));

import { usePageviewTracking, useOutboundClickTracking, useScrollDepthTracking } from "@/hooks/use-analytics";

// Small wrapper so the analytics hooks see the router context. Renders nothing.
// Three hooks: per-route pageview, global delegated outbound-click listener,
// per-page scroll-depth milestones (25/50/75/100%). All three skip /admin
// internally so admin traffic doesn't pollute customer-facing metrics.
const RouteTracker = () => {
  usePageviewTracking();
  useOutboundClickTracking();
  useScrollDepthTracking();
  return null;
};

// Lazy load non-critical global components
const LiveChat = lazy(() => import("./components/LiveChat"));
const TawkChat = lazy(() => import("./components/TawkChat").then(m => ({ default: m.TawkChat })));
// WhatsAppChat floating widget removed — channel disabled site-wide.
const CartDrawer = lazy(() => import("./components/CartDrawer"));

// Lazy load admin routes for better performance
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ContentManagement = lazy(() => import("./pages/admin/ContentManagement"));
const ProductCatalog = lazy(() => import("./pages/admin/ProductCatalog"));
const InquiryManagement = lazy(() => import("./pages/admin/InquiryManagement"));
const OrderManagement = lazy(() => import("./pages/admin/OrderManagement"));
const DocumentsPreview = lazy(() => import("./pages/admin/DocumentsPreview"));
const AIAgentManagement = lazy(() => import("./pages/admin/AIAgentManagement"));
const SEOManagement = lazy(() => import("./pages/admin/SEOManagement"));
const CampaignManager = lazy(() => import("./pages/admin/CampaignManager"));
const RegionalMarkets = lazy(() => import("./pages/admin/RegionalMarkets"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const IntegrationsManagement = lazy(() => import("./pages/admin/IntegrationsManagement"));
const ShareKit = lazy(() => import("./pages/admin/ShareKit"));
const ProductImageGenerator = lazy(() => import("./pages/admin/ProductImageGenerator"));
const CryptoWalletManagement = lazy(() => import("./pages/admin/CryptoWalletManagement"));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

// QueryClient is created lazily inside the component (not at module load)
// so that if the @tanstack/react-query chunk fails to resolve — e.g. a user
// has an old cached index.html that references a chunk filename we've since
// rotated — the failure happens INSIDE the React tree where error boundaries
// can catch it, rather than crashing the entire app shell with
// "TypeError: n is not a constructor". `useState` init callback runs once.
const App = () => {
  const [queryClient] = useState(() => new QueryClient());
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <Toaster />
            <Sonner />
            {/* Opt into the v7 transition behaviour now so the migration is
                already a no-op when react-router-dom@7 lands. Suppresses the
                two future-flag warnings that previously appeared on every
                navigation. */}
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              {/* LocaleProvider MUST be inside BrowserRouter — it uses
                  useLocation to read the current locale from the URL. */}
              <LocaleProvider>
              <RouteTracker />
              {/* ChunkErrorBoundary wraps the entire Routes tree — previously
                  only /admin was protected, so if a lazy-loaded PUBLIC chunk
                  failed to load after a deploy (Vite fingerprints change
                  and the browser cache has a stale reference), the app
                  blanked with no recovery UI. The boundary catches both
                  chunk-load errors AND generic render errors so visitors
                  see a "Reload page" button rather than a white screen. */}
              <ChunkErrorBoundary>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/about" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <About />
                  </Suspense>
                } />
                <Route path="/products" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Products />
                  </Suspense>
                } />
                {/* All 8 product-category landing pages share the Products
                    component but each renders its own SEO (title, desc,
                    canonical) derived from CATEGORY_BY_SLUG. Slugs:
                    human-apis · intermediates · veterinary · peptides ·
                    excipients · amino-acids · nootropics · research-chemicals.
                    Unknown slugs render the full catalog. */}
                <Route path="/products/:categorySlug" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Products />
                  </Suspense>
                } />
                {/* Product URL is `<slug>-<cas>` (e.g. l-glutamine-56-85-9)
                    for name-in-URL SEO. The bare-CAS form is still routed
                    to the same component for backwards compatibility;
                    ProductDetail parses either form via parseProductPath()
                    and its canonical tag always points at the slugged
                    URL, so old inbound links consolidate crawl signal on
                    the new canonical. .htaccess also 301s bare-CAS →
                    slugged for un-authenticated URL rewriting. */}
                <Route path="/product/:productPath" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <ProductDetail />
                  </Suspense>
                } />
                {/* Therapeutic-area landing pages — long-tail SEO for
                    buyer queries like "antiviral API supplier India". The
                    index route (/therapeutic with no :area) was previously
                    unmounted, so footer + CTA links to /therapeutic hit
                    the 404 page. Route it to Products with the default
                    "therapeutic" mode so users land on a browseable index. */}
                <Route path="/therapeutic" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Products />
                  </Suspense>
                } />
                <Route path="/therapeutic/:area" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <TherapeuticArea />
                  </Suspense>
                } />
                <Route path="/capabilities" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Capabilities />
                  </Suspense>
                } />
                <Route path="/quality" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Quality />
                  </Suspense>
                } />
                <Route path="/news" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <News />
                  </Suspense>
                } />
                {/* Per-article URL — required for individual indexing,
                    NewsArticle JSON-LD, and article-specific OG unfurls. */}
                {/* Tag-cluster landing page must come BEFORE the
                    catch-all /news/:slug route, otherwise React Router
                    would match "tag" as a slug and 404. */}
                <Route path="/news/tag/:tagSlug" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <NewsTag />
                  </Suspense>
                } />
                <Route path="/news/:slug" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <NewsArticle />
                  </Suspense>
                } />
                <Route path="/contact" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Contact />
                  </Suspense>
                } />
                <Route path="/checkout" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Checkout />
                  </Suspense>
                } />
                <Route path="/auth" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Auth />
                  </Suspense>
                } />
                <Route path="/auth/forgot-password" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <ForgotPassword />
                  </Suspense>
                } />
                <Route path="/auth/reset-password" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <ResetPassword />
                  </Suspense>
                } />
                <Route path="/privacy-policy" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <PrivacyPolicy />
                  </Suspense>
                } />
                <Route path="/return-policy" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <ReturnPolicy />
                  </Suspense>
                } />
                {/* High-intent B2B landing pages — dedicated SEO targets for
                    "GMP certified API supplier" + region-specific queries. */}
                <Route path="/certifications" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Certifications />
                  </Suspense>
                } />
                <Route path="/markets" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Markets />
                  </Suspense>
                } />
                <Route path="/markets/:regionSlug" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Markets />
                  </Suspense>
                } />
                {/* Free QC sample request — high-ROI low-commitment
                    capture funnel (Phase-2 audit fix). */}
                <Route path="/sample-request" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <SampleRequest />
                  </Suspense>
                } />
                {/* Country-specific landing pages — Phase-3 SEO play.
                    Captures "API supplier USA / Germany / Brazil / etc."
                    long-tail searches that /markets/:region averages
                    out. 8 countries seeded; add via COUNTRIES table in
                    src/pages/Country.tsx. */}
                <Route path="/country" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <CountryPage />
                  </Suspense>
                } />
                <Route path="/country/:countrySlug" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <CountryPage />
                  </Suspense>
                } />

                {/* Protected Admin Routes with Code Splitting.
                    Wrapped in ChunkErrorBoundary so that when we ship a new
                    build that removes the old hashed chunk filenames, users
                    with a cached older index.html get a friendly "Reload"
                    prompt instead of a blank page. */}
                <Route path="/admin" element={
                  <ProtectedRoute requireAdmin>
                    <ChunkErrorBoundary>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminLayout />
                      </Suspense>
                    </ChunkErrorBoundary>
                  </ProtectedRoute>
                }>
                  <Route index element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AdminDashboard />
                    </Suspense>
                  } />
                  <Route path="content" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ContentManagement />
                    </Suspense>
                  } />
                  <Route path="products" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ProductCatalog />
                    </Suspense>
                  } />
                  <Route path="inquiries" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InquiryManagement />
                    </Suspense>
                  } />
                  <Route path="orders" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <OrderManagement />
                    </Suspense>
                  } />
                  <Route path="documents" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <DocumentsPreview />
                    </Suspense>
                  } />
                  <Route path="ai-agents" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AIAgentManagement />
                    </Suspense>
                  } />
                  <Route path="seo" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <SEOManagement />
                    </Suspense>
                  } />
                  <Route path="campaigns" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CampaignManager />
                    </Suspense>
                  } />
                  <Route path="markets" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <RegionalMarkets />
                    </Suspense>
                  } />
                  <Route path="analytics" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Analytics />
                    </Suspense>
                  } />
                  <Route path="users" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <UserManagement />
                    </Suspense>
                  } />
                  <Route path="settings" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Settings />
                    </Suspense>
                  } />
                  <Route path="integrations" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <IntegrationsManagement />
                    </Suspense>
                  } />
                  <Route path="share-kit" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ShareKit />
                    </Suspense>
                  } />
                  <Route path="image-generator" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ProductImageGenerator />
                    </Suspense>
                  } />
                  <Route path="crypto-wallets" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CryptoWalletManagement />
                    </Suspense>
                  } />
                </Route>
                
                <Route path="*" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <NotFound />
                  </Suspense>
                } />
              </Routes>
              </ChunkErrorBoundary>

              {/* Global components - lazy loaded for better performance */}
              <Suspense fallback={null}>
                <LiveChat />
              </Suspense>
              {/* WhatsApp floating widget removed. Buyers land in the
                  contact form or email instead — single funnel, fewer
                  channels to monitor. */}
              {/* Catalog PDF lead-magnet — pops once per visitor on
                  30s OR 50% scroll. Internally skips admin / contact /
                  checkout / sample-request paths so it never interrupts
                  someone already mid-conversion. */}
              <Suspense fallback={null}>
                <CatalogLeadMagnet />
              </Suspense>
              <Suspense fallback={null}>
                <CartDrawer />
              </Suspense>
              {/* Tawk.to live chat — no-op unless VITE_TAWK_PROPERTY_ID
                  + VITE_TAWK_WIDGET_ID are set at build time. Deferred
                  behind first-interaction / 5s idle so it doesn't push
                  the LCP budget. */}
              <Suspense fallback={null}>
                <TawkChat />
              </Suspense>
              </LocaleProvider>
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;